import { FC, useCallback, useEffect, useState } from 'react';
import type { ImgHTMLAttributes } from 'react';
import { getHomePageDataByUrl } from '@/api/content';
import { CmsComponentProps } from '@/types/cms';
import useImpressionT2 from '@/tracks/useImpressionT2';
import useClickActionT2 from '@/tracks/useClickActionT2';
import ImpTracker from '@/components/common/ImpTracker';
import Container from '../Layout/container';
import { handleCmsJump } from '../../tools/cms-link';
import { useRouter } from 'next/router';
import { useCroppedImage } from '@weee-fe/module';
import { twMerge } from 'tailwind-merge';

/** Chromium `fetchpriority`; older `@types/react` omit it on `<img>`. */
type ImgAttrsWithFetchPriority = ImgHTMLAttributes<HTMLImageElement> & {
  fetchPriority?: 'high' | 'low' | 'auto';
};

/** 宫格内多图同时露出：全部 eager + high，避免 lazy 与低优先级拉长首屏拼齐时间 */
const bannerTileImgBoostProps = {
  loading: 'eager' as const,
  ...({ fetchPriority: 'high' } as Partial<ImgAttrsWithFetchPriority>)
};

const normalizeBannerArrayData = (source: any) => {
  if (Array.isArray(source)) {
    return source;
  }

  if (Array.isArray(source?.data)) {
    return source.data;
  }

  return [];
};

const BannerArray: FC<CmsComponentProps> = props => {
  const router = useRouter();
  const {
    skeleton,
    properties,
    postUrl,
    componentPos,
    trackingContext,
    cmsComponentKey,
    data,
    isStatic,
    className
  } = props;
  const { getSwitchedUrl } = useCroppedImage();
  const { bannerImpression } = useImpressionT2();
  const isStaticDataSource = Boolean(isStatic);
  const [load, setLoad] = useState(!isStaticDataSource);
  const [bannerData, setBannerData] = useState<any[]>(() => normalizeBannerArrayData(data));
  const { commonClick } = useClickActionT2();
  const eventKey = properties?.event_key || cmsComponentKey;

  const fetchData = useCallback(async () => {
    const res = await getHomePageDataByUrl(postUrl, {});
    if (res?.result) {
      setBannerData(normalizeBannerArrayData(res?.object));
    }
    setLoad(false);
  }, [postUrl]);

  useEffect(() => {
    if (isStaticDataSource) {
      setBannerData(normalizeBannerArrayData(data));
      setLoad(false);
      return;
    }

    if (!postUrl) {
      setLoad(false);
      return;
    }

    fetchData();
  }, [data, fetchData, isStaticDataSource, postUrl]);

  if (load) {
    return skeleton;
  }

  if (!load && !bannerData?.length) return null;

  return (
    <Container cmsComponentKey={cmsComponentKey} className={twMerge('pr-2.5 overflow-hidden', className)}>
      <div className="flex flex-wrap -mt-2.5">
        {bannerData.map((item, index) => {
          const bannerImpTrackParams = {
            mod_nm: eventKey,
            mod_pos: componentPos,
            co: {
              banner_id: item?.id || item?.key || '',
              banner_key: item?.id || item?.key || '',
              banner_pos: index,
              banner_type: cmsComponentKey,
              url: item?.link_url
            },
            ctx: trackingContext
          };
          const bannerClickTrackParams = {
            mod_nm: eventKey,
            mod_pos: componentPos,
            co: {
              target_nm: item?.key || item?.id || '',
              target_pos: index,
              target_type: cmsComponentKey,
              click_type: 'view',
              url: item?.link_url
            },
            ctx: trackingContext
          };
          const imageUrl = getSwitchedUrl(item.image_url);
          return (
            <a
              data-testid={`wid-collection-${cmsComponentKey}-${index}`}
              onClick={e => {
                if (item?.link_url) {
                  handleCmsJump(item.link_url, router);
                }
              }}
              key={`${item.key}-${index}`}
              className="w-1/2 pl-2.5 pt-2.5 flex-shrink-0 box-border"
              aria-label={item?.title || item?.key}>
              <ImpTracker
                impTrackingT2={() => {
                  bannerImpression(bannerImpTrackParams);
                }}
                clickTrackingT2={() => {
                  if (item?.link_url) {
                    commonClick(bannerClickTrackParams);
                  }
                }}>
                <div className="relative border border-solid border-[rgba(0,0,0,0.05)] box-border p-2.5 rounded-500 h-[90px] flex items-center overflow-hidden">
                  <div
                    className="absolute inset-0 z-0 flex justify-end items-center overflow-hidden rounded-500 pointer-events-none"
                    aria-hidden>
                    <img
                      src={imageUrl}
                      alt=""
                      className="h-full w-full max-w-none shrink-0 select-none overflow-hidden rounded-500"
                      draggable={false}
                      decoding="async"
                      {...bannerTileImgBoostProps}
                    />
                  </div>
                  <div className="relative z-[1] flex items-start flex-col w-full min-w-0">
                    <div className="enki-body-base-medium text-surface-100-fg-default max-w-[60%] whitespace-pre-line">
                      {item.title}
                    </div>
                    {item?.type === 'tags' && (
                      <div
                        className={`enki-body-3xs-strong h-5 rounded-500 px-2 flex items-center mt-2 text-pricing-fg max-w-full box-border`}
                        style={{ background: item.tags?.[0].background_color }}>
                        <span className="truncate">{item.tags?.[0].title}</span>
                      </div>
                    )}
                  </div>
                </div>
              </ImpTracker>
            </a>
          );
        })}
      </div>
    </Container>
  );
};

export default BannerArray;
